/* eslint-disable react-hooks/exhaustive-deps */
import './styles/App.scss';

// Utils
import i18next from 'i18next';
import { FC, Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getFromLocalStorageWithExpiry } from './utils/localstorage';
import { loadCatalogComposerIds } from './utils/composerRegistry';

// Components
import { ConfigProvider } from 'antd';
import { AppLayout } from './components/Layout';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';

// Redux
import { Provider } from 'react-redux';
import { uiActions } from './store/slices/ui';
import { PersistGate } from 'redux-persist/integration/react';
import { authActions, loginToSpotify } from './store/slices/auth';
import { persistor, store, useAppDispatch, useAppSelector } from './store/store';
import { MIX_BUILDER_ROUTE, selectLibrarySyncSettingsState, yourLibraryActions } from './store/slices/yourLibrary';
import { onboardingActions, selectOnboardingFlag } from './store/slices/onboarding';
import { bootPhaseActions, selectAllowLibraryNetwork } from './store/slices/bootPhase';
import { useLibraryBackgroundRefresh } from './hooks/useLibraryBackgroundRefresh';
import { useRecordingsAndWorksSyncMonitor } from './hooks/useRecordingsAndWorksSyncMonitor';
import { markAppReady } from './lib/appReadiness';
import { libraryCache } from './lib/cache/libraryCache';
import { backendClient } from './lib/api/backendClient';

// Spotify
import { PlaybackProvider, usePlayback } from './contexts/PlaybackContext';

// Pages
import SearchContainer from './pages/Search/Container';

const LIB_APP_LOG_ENABLED = true;
const LIB_APP_LOG_PREFIX = '[LibraryCache]';
const libAppLog = (...args: any[]) => {
  if (!LIB_APP_LOG_ENABLED) return;
  // eslint-disable-next-line no-console
  console.info(LIB_APP_LOG_PREFIX, ...args);
};

const Home = lazy(() => import('./pages/Home'));
const Page404 = lazy(() => import('./pages/404'));
const AlbumView = lazy(() => import('./pages/Album'));
const GenrePage = lazy(() => import('./pages/Genre'));
const BrowsePage = lazy(() => import('./pages/Browse'));
const ArtistPage = lazy(() => import('./pages/Artist'));
const PlaylistView = lazy(() => import('./pages/Playlist'));
const ArtistDiscographyPage = lazy(() => import('./pages/Discography'));
const WorkPage = lazy(() => import('./pages/Work'));
const RecordingPage = lazy(() => import('./pages/Recording'));
const ComposerPage = lazy(() => import('./pages/Composer'));
const ComposerWorksPage = lazy(() => import('./pages/ComposerWorks'));
const CustomMixLab = lazy(() => import('./pages/CustomMixLab'));
const SettingsMutesPage = lazy(() => import('./pages/Settings/MutesPage'));
const SettingsLibrarySyncPage = lazy(() => import('./pages/Settings/LibrarySyncPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorks'));

const Profile = lazy(() => import('./pages/User/Home'));
const ProfileTracks = lazy(() => import('./pages/User/Songs'));
const ProfileArtists = lazy(() => import('./pages/User/Artists'));
const ProfilePlaylists = lazy(() => import('./pages/User/Playlists'));
const YourPlaylistsPage = lazy(() => import('./pages/YourPlaylists'));
const FavouriteArtistsPage = lazy(() => import('./pages/FavouriteArtists'));
const FeaturedPlaylistsPage = lazy(() => import('./pages/FeaturedPlaylists'));
const NewReleasesPage = lazy(() => import('./pages/NewReleases'));
const RecentlyPlayedPage = lazy(() => import('./pages/RecentlyPlayed'));
const ArtistAppearsOnPage = lazy(() => import('./pages/ArtistAppearsOn'));
const ArtistFansAlsoLikePage = lazy(() => import('./pages/ArtistFansAlsoLike'));
const ArtistPopularPage = lazy(() => import('./pages/ArtistPopular'));
const ArtistMoreByPage = lazy(() => import('./pages/ArtistMoreBy'));

const SearchPage = lazy(() => import('./pages/Search/Home'));
const SearchTracks = lazy(() => import('./pages/Search/Songs'));
const SearchWorks = lazy(() => import('./pages/Search/Works'));
const LikedSongsPage = lazy(() => import('./pages/LikedSongs'));
const CallbackPage = lazy(() => import('./pages/Callback'));
const SearchAlbums = lazy(() => import('./pages/Search/Albums'));
const SearchPlaylist = lazy(() => import('./pages/Search/Playlists'));
const SearchPageArtists = lazy(() => import('./pages/Search/Artists'));
const RecentlySearched = lazy(() => import('./pages/Search/RecentlySearched'));

const AUTH_TRACE = '[AuthTrace]';

window.addEventListener('resize', () => {
  const vh = window.innerWidth;
  if (vh < 950) {
    store.dispatch(uiActions.collapseLibrary());
  }
});

const SpotifyContainer: FC<{ children: any }> = memo(({ children }) => {
  const dispatch = useAppDispatch();

  const hasBootstrappedAuth = useRef(false);
  const isCallbackRoute =
    typeof window !== 'undefined' &&
    (window.location.pathname === '/callback' || new URLSearchParams(window.location.search).has('code'));
  const isHowItWorksRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/how-it-works');

  useEffect(() => {
    if (isCallbackRoute) {
      // Don't process auth here if we're on callback route - let CallbackPage handle it
      console.info(`${AUTH_TRACE} bootstrap skipped on callback route`);
      return;
    }

    // Guard against React 18 StrictMode double-invocation in development
    if (hasBootstrappedAuth.current) return;
    hasBootstrappedAuth.current = true;

    const tokenInLocalStorage = getFromLocalStorageWithExpiry('access_token');
    console.info(`${AUTH_TRACE} bootstrap start`, {
      hasToken: !!tokenInLocalStorage,
      isCallbackRoute,
    });
    dispatch(authActions.setToken({ token: tokenInLocalStorage }));

    if (tokenInLocalStorage) {
      console.info(`${AUTH_TRACE} bootstrap fetchUser`);
      dispatch(authActions.fetchUser());
    } else if (isHowItWorksRoute) {
      console.info(`${AUTH_TRACE} bootstrap skip login on /how-it-works`);
      dispatch(authActions.setRequesting({ requesting: false }));
    } else {
      console.info(`${AUTH_TRACE} bootstrap dispatch loginToSpotify`);
      dispatch(loginToSpotify(false));
    }
  }, [dispatch, isCallbackRoute, isHowItWorksRoute]);

  // Spinner temporarily disabled; always render children
  return <PlaybackProvider>{children}</PlaybackProvider>;
});

const RoutesComponent = memo(() => {
  const location = useLocation();
  const container = useRef<HTMLDivElement>(null);
  const user = useAppSelector((state) => !!state.auth.user);
  const lastNavigationRef = useRef<{ key: string | undefined; timestamp: number }>({
    key: undefined,
    timestamp: typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now(),
  });

  const readNow = useCallback(() => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }, []);

  if (location.key !== lastNavigationRef.current.key) {
    const timestamp = readNow();
    lastNavigationRef.current = { key: location.key, timestamp };
    if (typeof window !== 'undefined') {
      (window as any).__CMF_ROUTE_NAVIGATION_START = timestamp;
    }
  }

  useEffect(() => {
    if (container.current) {
      container.current.scrollTop = 0;
    }
  }, [location, container]);

  const routes = useMemo(
    () =>
      [
        { path: '', element: <Home container={container} />, public: true },
        { public: true, path: '/callback', element: <CallbackPage /> },
        { path: '/collection/tracks', element: <LikedSongsPage container={container} /> },
        {
          public: true,
          path: '/playlist/:playlistId',
          element: <PlaylistView container={container} />,
        },
        { path: '/album/:albumId', element: <AlbumView container={container} /> },
        { public: true, path: '/work/:workId', element: <WorkPage container={container} /> },
        { public: true, path: '/recording/:recordingId', element: <RecordingPage container={container} /> },
        { public: true, path: '/composer/:composerId', element: <ComposerPage container={container} /> },
        { public: true, path: '/composer/:composerId/works', element: <ComposerWorksPage container={container} /> },
        {
          public: true,
          path: '/composer/:composerId/works/saved',
          element: <Navigate to='../works?view=saved' replace />,
        },
        {
          path: '/artist/:artistId/discography',
          element: <ArtistDiscographyPage container={container} />,
        },
        {
          path: '/artist/:artistId/appears-on',
          element: <ArtistAppearsOnPage container={container} />,
        },
        {
          path: '/artist/:artistId/fans-also-like',
          element: <ArtistFansAlsoLikePage container={container} />,
        },
        { path: '/artist/:artistId/popular', element: <ArtistPopularPage container={container} /> },
        {
          path: '/artist/:artistId/more-by-artist',
          element: <ArtistMoreByPage container={container} />,
        },
        { public: true, path: '/artist/:artistId', element: <ArtistPage container={container} /> },
        { path: '/your-playlists', element: <YourPlaylistsPage container={container} /> },
        { path: '/favourite-artists', element: <FavouriteArtistsPage container={container} /> },
        { path: '/featured-playlists', element: <FeaturedPlaylistsPage container={container} /> },
        { path: '/new-releases', element: <NewReleasesPage container={container} /> },
        { path: '/recently-played', element: <RecentlyPlayedPage container={container} /> },
        { path: '/users/:userId/artists', element: <ProfileArtists container={container} /> },
        { path: '/users/:userId/playlists', element: <ProfilePlaylists container={container} /> },
        { path: '/users/:userId/tracks', element: <ProfileTracks container={container} /> },
        { path: '/users/:userId', element: <Profile container={container} /> },
        { path: '/settings/mutes', element: <SettingsMutesPage /> },
        { path: '/settings/library-sync', element: <SettingsLibrarySyncPage /> },
        { public: true, path: '/genre/:genreId', element: <GenrePage /> },
        { public: true, path: '/search', element: <BrowsePage /> },
        { path: '/recent-searches', element: <RecentlySearched /> },
        { path: MIX_BUILDER_ROUTE, element: <CustomMixLab /> },
        { path: '/tools/custom-mix', element: <Navigate to={MIX_BUILDER_ROUTE} replace /> },
        {
          public: true,
          path: '/search/:search',
          element: <SearchContainer container={container} />,
          children: [
            {
              path: 'artists',
              element: <SearchPageArtists container={container} />,
            },
            {
              path: 'albums',
              element: <SearchAlbums container={container} />,
            },
            {
              path: 'playlists',
              element: <SearchPlaylist container={container} />,
            },
            {
              path: 'tracks',
              element: <SearchTracks container={container} />,
            },
            {
              path: 'works',
              element: <SearchWorks container={container} />,
            },
            {
              path: '',
              element: <SearchPage container={container} />,
            },
          ],
        },
        { public: true, path: '/how-it-works', element: <HowItWorksPage /> },
        { path: '*', element: <Page404 /> },
      ].filter((r) => (user ? true : r.public)),
    [container, user]
  );

  return (
    <div
      className='Main-section'
      ref={container}
      style={{
        height: user ? undefined : `calc(100vh - 50px)`,
      }}
    >
      <div
        style={{
          minHeight: user ? 'calc(100vh - 230px)' : 'calc(100vh - 100px)',
          width: '100%',
        }}
      >
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<Suspense>{route.element}</Suspense>}
            >
              {route?.children
                ? route.children.map((child) => (
                    <Route
                      key={child.path}
                      path={child.path}
                      element={<Suspense>{child.element}</Suspense>}
                    />
                  ))
                : undefined}
            </Route>
          ))}
        </Routes>
      </div>
    </div>
  );
});

const RootComponent = () => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((state) => state.auth.user);
  const user = !!authUser;
  const language = useAppSelector((state) => state.language.language);
  const { status: librarySyncStatus, lastFetchedAt: librarySyncLastFetchedAt } = useAppSelector(
    selectLibrarySyncSettingsState
  );
  const librarySyncState = useAppSelector(selectLibrarySyncSettingsState);
  const importStatus = useAppSelector((state) => state.yourLibrary.importStatus ?? 'idle');
  const lastImportStartedAt = useAppSelector((state) => state.yourLibrary.lastImportStartedAt ?? null);
  const lastImportRequiresAlbumSync = useAppSelector(
    (state) => state.yourLibrary.lastImportRequiresAlbumSync ?? null
  );
  const lastImportRequiresTrackSync = useAppSelector(
    (state) => state.yourLibrary.lastImportRequiresTrackSync ?? null
  );
  const importSyncSelections = useAppSelector((state) => state.yourLibrary.importSyncSelections);
  const importCompletionFlag = useAppSelector((state) =>
    selectOnboardingFlag(state, 'importCompletion')
  );
  const onboardingStatus = useAppSelector((state) => state.onboarding.status);
  const allowLibraryNetwork = useAppSelector(selectAllowLibraryNetwork);
  const libraryFilter = useAppSelector((state) => state.yourLibrary.filter);
  const myAlbumsCount = useAppSelector((state) => state.yourLibrary.myAlbums.length);
  const myArtistsCount = useAppSelector((state) => state.yourLibrary.myArtists.length);
  const myPlaylistsCount = useAppSelector((state) => state.yourLibrary.myPlaylists.length);
  const myComposersCount = useAppSelector((state) => state.yourLibrary.myComposers.length);
  const [fullCacheAttempted, setFullCacheAttempted] = useState(false);
  const bootCacheLoadedRef = useRef(false);
  const fullCacheLoadedRef = useRef(false);
  const worksRecordingsCacheLoadedRef = useRef(false);
  const worksRecordingsFetchTriggeredRef = useRef(false);
  const coreCacheFetchTriggeredRef = useRef(false);
  const onboardingCacheLoadedRef = useRef(false);
  const onboardingFetchTriggeredRef = useRef(false);
  const lastHydratedUserRef = useRef<string | null>(null);
  const lastLibrarySyncAttemptRef = useRef<number | null>(null);
  const lastImportHandledRef = useRef<number | null>(null);
  const profileSyncRef = useRef<string | null>(null);
  const { isPlaying, pauseTrack, resumeTrack, status, queueStatus } = usePlayback();
  useLibraryBackgroundRefresh(allowLibraryNetwork);
  // Allow manual import/sync flows to always poll for progress, even before boot phase unlocks.
  useRecordingsAndWorksSyncMonitor(true);
  const catalogComposersLoadedRef = useRef<string | null>(null);

  useEffect(() => {
    const startedAt = lastImportStartedAt;
    if (!startedAt) return;
    if (importStatus !== 'succeeded') return;

    const requireAlbum =
      lastImportRequiresAlbumSync ?? importSyncSelections.syncRecordings ?? false;
    const requireTrack =
      lastImportRequiresTrackSync ?? importSyncSelections.syncTrackRecordings ?? false;

    const parseIsoToTimestamp = (value?: string | null) => {
      if (!value) return null;
      const timestamp = Date.parse(value);
      return Number.isFinite(timestamp) ? timestamp : null;
    };

    const job = librarySyncState.recordingsAndWorksJob;
    const jobStatus = job?.status;
    const jobFinishedAt = parseIsoToTimestamp(job?.finishedAt ?? null);

    const albumLastRunAt = parseIsoToTimestamp(
      librarySyncState.value.oneOffs.syncAlbumRecordings.lastRunAt
    );
    const albumLastStatus = librarySyncState.value.oneOffs.syncAlbumRecordings.lastStatus;

    const trackLastRunAt = parseIsoToTimestamp(
      librarySyncState.value.oneOffs.syncTrackRecordings?.lastRunAt ?? null
    );
    const trackLastStatus =
      librarySyncState.value.oneOffs.syncTrackRecordings?.lastStatus ?? null;

    const albumSatisfied =
      !requireAlbum ||
      ((jobStatus === 'succeeded' &&
        jobFinishedAt !== null &&
        jobFinishedAt >= startedAt) ||
        (albumLastStatus === 'success' && albumLastRunAt !== null && albumLastRunAt >= startedAt));

    const trackSatisfied =
      !requireTrack ||
      ((jobStatus === 'succeeded' &&
        jobFinishedAt !== null &&
        jobFinishedAt >= startedAt) ||
        (trackLastStatus === 'success' && trackLastRunAt !== null && trackLastRunAt >= startedAt));

    if (!albumSatisfied || !trackSatisfied) return;

    const flagSeen = Boolean(importCompletionFlag?.seenAt);
    const canDecide = flagSeen || importCompletionFlag !== null || onboardingStatus !== 'idle';
    if (!canDecide) return;
    if (flagSeen) return;
    if (lastImportHandledRef.current === startedAt) return;
    lastImportHandledRef.current = startedAt;

    dispatch(uiActions.openImportCompletion());
    dispatch(
      onboardingActions.markOnboardingSeen({
        key: 'importCompletion',
        version: importCompletionFlag?.version ?? 1,
      })
    );
  }, [
    dispatch,
    importStatus,
    lastImportStartedAt,
    lastImportRequiresAlbumSync,
    lastImportRequiresTrackSync,
    importSyncSelections.syncRecordings,
    importSyncSelections.syncTrackRecordings,
    librarySyncState.recordingsAndWorksJob,
    librarySyncState.value.oneOffs.syncAlbumRecordings.lastRunAt,
    librarySyncState.value.oneOffs.syncAlbumRecordings.lastStatus,
    librarySyncState.value.oneOffs.syncTrackRecordings?.lastRunAt,
    librarySyncState.value.oneOffs.syncTrackRecordings?.lastStatus,
    importCompletionFlag?.seenAt,
    importCompletionFlag?.version,
    onboardingStatus,
  ]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
    i18next.changeLanguage(language);
  }, [language]);

  useEffect(() => {
    const id = requestAnimationFrame(() => markAppReady());
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    // Reset cache hydration markers when user changes
    const currentUserId = authUser?.id ?? null;
    if (lastHydratedUserRef.current !== currentUserId) {
      libAppLog('user changed; reset cache hydration markers', { currentUserId });
      bootCacheLoadedRef.current = false;
      fullCacheLoadedRef.current = false;
      worksRecordingsCacheLoadedRef.current = false;
      worksRecordingsFetchTriggeredRef.current = false;
      coreCacheFetchTriggeredRef.current = false;
      onboardingCacheLoadedRef.current = false;
      onboardingFetchTriggeredRef.current = false;
      lastHydratedUserRef.current = currentUserId;
      dispatch(onboardingActions.resetForUser());
    }
  }, [authUser, dispatch]);

  useEffect(() => {
    const userKey = authUser?.id ?? 'anon';
    if (!allowLibraryNetwork) return;
    if (catalogComposersLoadedRef.current === userKey) return;
    catalogComposersLoadedRef.current = userKey;
    void loadCatalogComposerIds().catch(() => undefined);
  }, [allowLibraryNetwork, authUser?.id]);

  useEffect(() => {
    if (bootCacheLoadedRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;
    bootCacheLoadedRef.current = true;
    const expectedUserId = userId;
    libAppLog('load boot cache start', { userId });
    void libraryCache.loadBoot(userId).then((cached) => {
      if (authUser?.id !== expectedUserId) {
        libAppLog('load boot cache abort: user changed', { expectedUserId, currentUserId: authUser?.id });
        return;
      }
      if (!cached) return;
      if (cached.artists?.length) {
        dispatch(yourLibraryActions.upsertMyArtists(cached.artists));
      }
      if (cached.playlists?.length) {
        dispatch(yourLibraryActions.upsertMyPlaylists(cached.playlists));
      }
      if (cached.albums?.length) {
        dispatch(yourLibraryActions.upsertMyAlbums(cached.albums));
      }
      if (cached.composers?.length) {
        dispatch(yourLibraryActions.setMyComposers(cached.composers));
      }
      if (cached.works?.length) {
        dispatch(yourLibraryActions.upsertMyWorks(cached.works));
      }
      if (cached.recordings?.length) {
        dispatch(yourLibraryActions.upsertMyRecordings(cached.recordings));
      }
      libAppLog('load boot cache applied', {
        userId,
        artists: cached.artists?.length ?? 0,
        playlists: cached.playlists?.length ?? 0,
        albums: cached.albums?.length ?? 0,
        composers: cached.composers?.length ?? 0,
        works: cached.works?.length ?? 0,
        recordings: cached.recordings?.length ?? 0,
      });
    });
  }, [authUser?.id, dispatch]);

  useEffect(() => {
    if (onboardingCacheLoadedRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;
    onboardingCacheLoadedRef.current = true;
    void dispatch(onboardingActions.hydrateOnboardingFromCache(userId));
  }, [authUser?.id, dispatch]);

  useEffect(() => {
    if (!allowLibraryNetwork) return;
    if (fullCacheLoadedRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;
    fullCacheLoadedRef.current = true;
    setFullCacheAttempted(true);
    const expectedUserId = userId;
    libAppLog('load full cache start', { userId });
    void libraryCache.loadFull(userId).then((cached) => {
      if (authUser?.id !== expectedUserId) {
        libAppLog('load full cache abort: user changed', { expectedUserId, currentUserId: authUser?.id });
        return;
      }
      if (!cached) return;
      if (cached.artists?.length) {
        dispatch(yourLibraryActions.upsertMyArtists(cached.artists));
      }
      if (cached.playlists?.length) {
        dispatch(yourLibraryActions.upsertMyPlaylists(cached.playlists));
      }
      if (cached.albums?.length) {
        dispatch(yourLibraryActions.upsertMyAlbums(cached.albums));
      }
      if (cached.composers?.length) {
        dispatch(yourLibraryActions.setMyComposers(cached.composers));
      }
      libAppLog('load full cache applied', {
        userId,
        artists: cached.artists?.length ?? 0,
        playlists: cached.playlists?.length ?? 0,
        albums: cached.albums?.length ?? 0,
        composers: cached.composers?.length ?? 0,
      });
    });
  }, [allowLibraryNetwork, authUser?.id, dispatch]);

  useEffect(() => {
    if (!allowLibraryNetwork) return;
    if (worksRecordingsCacheLoadedRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;
    const shouldLoad =
      fullCacheLoadedRef.current ||
      libraryFilter === 'WORKS' ||
      libraryFilter === 'RECORDINGS';
    if (!shouldLoad) return;

    worksRecordingsCacheLoadedRef.current = true;
    const expectedUserId = userId;
    libAppLog('load works/recordings cache start', { userId });
    void libraryCache.loadFullWorksRecordings(userId).then((cached) => {
      if (authUser?.id !== expectedUserId) {
        libAppLog('load works/recordings cache abort: user changed', {
          expectedUserId,
          currentUserId: authUser?.id,
        });
        return;
      }

      const worksCount = cached?.works?.length ?? 0;
      const recordingsCount = cached?.recordings?.length ?? 0;

      if (worksCount) {
        dispatch(yourLibraryActions.upsertMyWorks(cached!.works!));
      }
      if (recordingsCount) {
        dispatch(yourLibraryActions.upsertMyRecordings(cached!.recordings!));
      }

      libAppLog('load works/recordings cache applied', {
        userId,
        works: worksCount,
        recordings: recordingsCount,
      });

      const missingWorks = worksCount === 0;
      const missingRecordings = recordingsCount === 0;

      if ((missingWorks || missingRecordings) && allowLibraryNetwork && !worksRecordingsFetchTriggeredRef.current) {
        worksRecordingsFetchTriggeredRef.current = true;
        if (missingWorks) {
          dispatch(yourLibraryActions.fetchMyWorks());
        }
        if (missingRecordings) {
          dispatch(yourLibraryActions.fetchMyRecordings());
        }
      }
    });
  }, [allowLibraryNetwork, authUser?.id, dispatch, libraryFilter]);

  useEffect(() => {
    if (!allowLibraryNetwork) return;
    if (onboardingFetchTriggeredRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;
    onboardingFetchTriggeredRef.current = true;
    dispatch(onboardingActions.loadOnboardingFlags());
  }, [allowLibraryNetwork, authUser?.id, dispatch]);

  useEffect(() => {
    if (!allowLibraryNetwork) return;
    const userId = authUser?.id;
    if (!userId) return;

    const images = authUser?.images;
    const imageUrl =
      Array.isArray(images) && images.length > 0 && typeof images[0]?.url === 'string'
        ? images[0]!.url
        : null;

    const payload = {
      displayName: authUser?.display_name ?? null,
      email: authUser?.email ?? null,
      profileUrl: authUser?.external_urls?.spotify ?? null,
      imageUrl
    };

    const cacheKey = [userId, payload.displayName ?? '', payload.email ?? '', payload.profileUrl ?? '', payload.imageUrl ?? ''].join(
      '|'
    );
    if (profileSyncRef.current === cacheKey) return;
    profileSyncRef.current = cacheKey;

    void backendClient.post('/user/profile', payload).catch((error) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[UserProfileSync] failed to persist profile', error);
      }
      profileSyncRef.current = null;
    });
  }, [
    allowLibraryNetwork,
    authUser?.id,
    authUser?.display_name,
    authUser?.email,
    authUser?.external_urls,
    authUser?.images
  ]);

  useEffect(() => {
    if (!allowLibraryNetwork) return;
    if (!fullCacheLoadedRef.current) return;
    if (coreCacheFetchTriggeredRef.current) return;
    const userId = authUser?.id;
    if (!userId) return;

    const coreCounts = {
      albums: myAlbumsCount,
      artists: myArtistsCount,
      playlists: myPlaylistsCount,
      composers: myComposersCount,
    };
    const hasCoreData = Object.values(coreCounts).some((count) => count > 0);
    const forceDbFallback =
      process.env.NODE_ENV === 'development' && (window as any)?.__CMF_FORCE_DB_FALLBACK === true;
    const readyForFallback = fullCacheAttempted || fullCacheLoadedRef.current;
    if (hasCoreData && !forceDbFallback) return;
    if (!readyForFallback) return;

    coreCacheFetchTriggeredRef.current = true;
    libAppLog('core cache empty/forced; triggering backend fetch', { userId, coreCounts, forceDbFallback });

    void (async () => {
      try {
        await dispatch(yourLibraryActions.fetchLibraryCoreFromDb()).unwrap();
      } catch (error) {
        libAppLog('core cache fallback failed', { error });
      }
    })();
  }, [
    allowLibraryNetwork,
    authUser?.id,
    dispatch,
    myAlbumsCount,
    myArtistsCount,
    myPlaylistsCount,
    myComposersCount,
    fullCacheAttempted,
  ]);

  useEffect(() => {
    if (!user) return;
    if (!allowLibraryNetwork) return;
    const staleAfterMs = 5 * 60 * 1000;
    const retryAfterErrorMs = 60 * 1000;
    const now = Date.now();

    const lastAttemptAt = lastLibrarySyncAttemptRef.current;
    const isStale =
      librarySyncLastFetchedAt != null ? now - librarySyncLastFetchedAt > staleAfterMs : true;
    const shouldRetryError =
      librarySyncStatus === 'error' &&
      (lastAttemptAt === null || now - lastAttemptAt > retryAfterErrorMs);
    const shouldLoad =
      librarySyncStatus === 'idle' ||
      shouldRetryError ||
      (librarySyncStatus === 'ready' && isStale);

    if (shouldLoad) {
      lastLibrarySyncAttemptRef.current = now;
      dispatch(yourLibraryActions.loadLibrarySyncSettings());
    }
  }, [dispatch, user, librarySyncStatus, librarySyncLastFetchedAt, allowLibraryNetwork]);

  useEffect(() => {
    if (status === 'ready' && (queueStatus === 'ready' || queueStatus === 'idle')) {
      dispatch(bootPhaseActions.setPlaybackReady());
      dispatch(bootPhaseActions.setLibraryAllowed());
    }
  }, [status, queueStatus, dispatch]);

  const handleSpaceBar = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toUpperCase();
      // Ignore spacebar when focus is inside form fields or contentEditable (e.g., feedback textarea).
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (status !== 'ready') return;
      e.stopPropagation();
      if (e.key === ' ' || e.code === 'Space' || e.keyCode === 32) {
        e.preventDefault();
        if (isPlaying) {
          pauseTrack().catch(() => {});
        } else {
          resumeTrack().catch(() => {});
        }
      }
    },
    [isPlaying, pauseTrack, resumeTrack, status]
  );

  useEffect(() => {
    if (!user) return;
    document.addEventListener('keydown', handleSpaceBar);
    return () => {
      document.removeEventListener('keydown', handleSpaceBar);
    };
  }, [user, handleSpaceBar]);

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <AppLayout>
        <RoutesComponent />
      </AppLayout>
    </Router>
  );
};

function App() {
  return (
    <ConfigProvider theme={{ token: { fontFamily: 'SpotifyMixUI' } }}>
      <Provider store={store} stabilityCheck='never' identityFunctionCheck='never'>
        <PersistGate loading={null} persistor={persistor}>
          <SpotifyContainer>
            <RootComponent />
          </SpotifyContainer>
        </PersistGate>
      </Provider>
    </ConfigProvider>
  );
}

export default App;
